/**
 * v2.1 Stiff and Sore scoring — the second reported axis.
 *
 * The capacity math is untouched by everything here. This module reads a
 * region's capacity percent alongside its Stiff and Sore answers and produces
 * a *state*, which is what makes the early-signal person visible.
 *
 * The problem it solves: under v2.0.0 a worker with mild interference on three
 * items scores 95% capacity, lands in Full capacity, and is told to "maintain
 * and optimize" — while reporting a 3/10 ache he has noticed five days out of
 * seven and which is worse than a year ago. He is the person the program
 * exists to find, and the instrument reported him as fine.
 *
 * See epic #67 and issue #69.
 */

import assessmentData from "@/data/fit2lift_uptime_assessment_v2.json";

export type StiffSoreBand = "clear" | "whisper" | "early" | "loud" | "severe";

export type RegionState =
  | "maintain"
  | "watch"
  | "early_signal"
  | "masking"
  | "deconditioned"
  | "adapted"
  | "active"
  | "priority";

export type Trajectory = "better" | "same" | "worse" | "unsure";

export type Descriptor =
  | "stiff"
  | "achy"
  | "sharp"
  | "burning"
  | "numb_tingling"
  | "weak";

export type RegionSignal = {
  regionKey: string;
  /** 0-100, from the existing unchanged capacity math. */
  capacityPercent: number;
  /** 0-10, worst over the past 7 days. NOT an average. */
  stiffSoreWorst: number;
  /** 0-7, days noticed in the last week. */
  stiffSoreDays7: number;
  /**
   * Every descriptor the respondent picked, in tap order. Empty when they
   * skipped it or the region was screened out.
   *
   * Multi-select since v2.2: an ache is commonly stiff AND achy, or sharp AND
   * burning, and forcing one discarded the rest. The descriptor routes
   * programming, so the discarded ones mattered.
   */
  descriptors: Descriptor[];
  trajectory: Trajectory | null;
  screenedOut: boolean;
};

/**
 * Worst state wins when rolling six regions up to one person.
 *
 * `masking` outranks `early_signal` deliberately: full capacity reported
 * alongside a loud ache means the respondent is either under-reporting
 * interference or has adapted around something significant, and that warrants
 * a human conversation ahead of a mild early signal. Locked as D2 on #67.
 */
export const REGION_STATE_SEVERITY = [
  "priority",
  "active",
  "masking",
  "early_signal",
  "adapted",
  "deconditioned",
  "watch",
  "maintain",
] as const satisfies readonly RegionState[];

export function stiffSoreBandFor(worst: number): StiffSoreBand {
  const clamped = Math.max(0, Math.min(10, Math.round(worst)));
  const band = assessmentData.stiff_sore_bands.find(
    (b) => clamped >= b.min && clamped <= b.max
  );
  return (band?.key ?? "clear") as StiffSoreBand;
}

type CapacityRow = "cap_92_100" | "cap_85_91" | "cap_70_84" | "cap_under_70";
type SoreColumn = "ss_0" | "ss_1_2" | "ss_3_5" | "ss_6_10";

function capacityRowFor(percent: number): CapacityRow {
  if (percent >= 92) return "cap_92_100";
  if (percent >= 85) return "cap_85_91";
  if (percent >= 70) return "cap_70_84";
  return "cap_under_70";
}

function soreColumnFor(band: StiffSoreBand): SoreColumn {
  if (band === "clear") return "ss_0";
  if (band === "whisper") return "ss_1_2";
  if (band === "early") return "ss_3_5";
  return "ss_6_10";
}

/**
 * The state matrix, as a literal table so a reviewer can diff it against the
 * build spec line by line. Do not rewrite this as nested conditionals.
 *
 * Two cells carry the product:
 *   early_signal — capacity intact, ache present. The only place prevention is
 *                  cheap. Did not exist anywhere in the system before v2.1.
 *   masking      — full capacity alongside a loud ache. Worth a conversation.
 */
const STATE_MATRIX: Record<CapacityRow, Record<SoreColumn, RegionState>> = {
  cap_92_100: {
    ss_0: "maintain",
    ss_1_2: "early_signal",
    ss_3_5: "early_signal",
    ss_6_10: "masking",
  },
  cap_85_91: {
    ss_0: "watch",
    ss_1_2: "early_signal",
    ss_3_5: "early_signal",
    ss_6_10: "active",
  },
  cap_70_84: {
    ss_0: "deconditioned",
    ss_1_2: "active",
    ss_3_5: "active",
    ss_6_10: "active",
  },
  cap_under_70: {
    ss_0: "adapted",
    ss_1_2: "active",
    ss_3_5: "priority",
    ss_6_10: "priority",
  },
};

export function regionStateFor(signal: RegionSignal): RegionState {
  const row = capacityRowFor(signal.capacityPercent);
  const col = soreColumnFor(stiffSoreBandFor(signal.stiffSoreWorst));
  return STATE_MATRIX[row][col];
}

/**
 * Capacity still intact, ache present. Spans both the 92-100 and 85-91 rows,
 * matching the two `early_signal` columns of the matrix.
 */
export function isEarlySignal(signal: RegionSignal): boolean {
  return (
    signal.stiffSoreWorst >= 1 &&
    signal.stiffSoreWorst <= 5 &&
    signal.capacityPercent >= 85
  );
}

/** Persistent low-grade is the classic pre-injury pattern, whatever the peak. */
export function isPersistentLowGrade(signal: RegionSignal): boolean {
  return signal.stiffSoreDays7 >= 5;
}

/**
 * Never let this sit quietly inside an aggregate. It needs a referral.
 *
 * Fires if numbness or tingling is among the descriptors, not only if it was
 * the single one chosen — the whole point of allowing several is that a
 * respondent can report "achy AND numb", and the neurological half is the half
 * that matters.
 */
export function isNeuroFlag(signal: RegionSignal): boolean {
  return signal.descriptors.includes("numb_tingling");
}

/** The highest-priority individual finding the instrument can produce. */
export function isEarlyAndWorsening(signal: RegionSignal): boolean {
  return isEarlySignal(signal) && signal.trajectory === "worse";
}

export function personStateFor(signals: RegionSignal[]): RegionState {
  if (signals.length === 0) return "maintain";
  const states = new Set(signals.map(regionStateFor));
  return REGION_STATE_SEVERITY.find((s) => states.has(s)) ?? "maintain";
}

/**
 * Report order. Ties break on `regionKey` so output is deterministic and
 * snapshot tests cannot flake.
 */
export function rankRegionsForReport<T extends RegionSignal>(signals: T[]): T[] {
  // Generic so callers can rank their own richer shape (a signal carrying a
  // display label, say) without losing the extra fields on the way through.
  const severityIndex = (s: RegionSignal) =>
    REGION_STATE_SEVERITY.indexOf(regionStateFor(s));
  return [...signals].sort((a, b) => {
    const worsening = Number(isEarlyAndWorsening(b)) - Number(isEarlyAndWorsening(a));
    if (worsening !== 0) return worsening;
    const severity = severityIndex(a) - severityIndex(b);
    if (severity !== 0) return severity;
    const persistent =
      Number(isPersistentLowGrade(b)) - Number(isPersistentLowGrade(a));
    if (persistent !== 0) return persistent;
    const worst = b.stiffSoreWorst - a.stiffSoreWorst;
    if (worst !== 0) return worst;
    return a.regionKey.localeCompare(b.regionKey);
  });
}

/**
 * Which measurement a stored `pain_score_*` value actually holds.
 *
 * D1 on #67: the Stiff and Sore Score reuses the existing columns rather than
 * adding new ones, so those columns hold "average pain over the past week" for
 * v2.0.0 rows and "worst in the past 7 days" for v2.1 rows. Two different
 * measurements in one column, separated only by the version stamp.
 *
 * Every read path over `pain_score_*` must route through here. Averaging the
 * two eras together produces a number that means nothing (BLOCKER-B on #67).
 */
export type PainEra = "average_v2_0" | "worst_v2_1";

/**
 * Which SCORING MODEL produced a stored capacity percentage.
 *
 * v2.2.0 retired Q9 from every region, so a region is now eleven items out of
 * 55 before it and ten out of 50 after it. Both produce a 0-100 percentage, but
 * they are percentages of different things and of different question sets.
 *
 * Capacity was comparable across the 2.0.0 -> 2.1.0 boundary and deliberately
 * kept so. It is NOT comparable across 2.2.0. Any trend, retest delta or
 * cross-era average over `score_*` must be split on this, exactly as
 * `pain_score_*` is split on `painEraFor`.
 */
export type CapacityEra = "items11_max55" | "items10_max50";

export function capacityEraFor(
  assessmentVersion: string | null | undefined
): CapacityEra {
  return atLeast(assessmentVersion, 2, 2) ? "items10_max50" : "items11_max55";
}

/** Segment-wise version compare. A lexical one sorts "2.10.0" below "2.1.0". */
function atLeast(
  version: string | null | undefined,
  major: number,
  minor: number
): boolean {
  if (!version) return false;
  const parts = version.split(".").map((n) => Number.parseInt(n, 10));
  if (parts.some((n) => !Number.isFinite(n))) return false;
  const [maj = 0, min = 0] = parts;
  if (maj > major) return true;
  return maj === major && min >= minor;
}

export function painEraFor(assessmentVersion: string | null | undefined): PainEra {
  return atLeast(assessmentVersion, 2, 1) ? "worst_v2_1" : "average_v2_0";
}

/** Server payload shape for one region's Stiff and Sore answers. */
export type RegionSignalPayload = {
  stiff_sore_worst: number;
  stiff_sore_days_7: number;
  descriptors: string[];
  trajectory: string | null;
  screened_out: boolean;
};

/**
 * Builds the `region_signals` payload block.
 *
 * Extracted from the questionnaire component so the screener-equivalence
 * guarantee is testable: a screened-out region must produce exactly what a
 * respondent answering all eleven items with 0 produces, differing only in the
 * `screened_out` flag. If those two ever diverge, the screener stops being a
 * shortcut through the same instrument and becomes a second instrument.
 */
export function buildRegionSignalsPayload(
  regionKeys: readonly string[],
  state: {
    worst: Record<string, number>;
    days: Record<string, number>;
    descriptors: Record<string, string[]>;
    trajectory: Record<string, string>;
    screenedOut: Record<string, boolean>;
  }
): Record<string, RegionSignalPayload> {
  return Object.fromEntries(
    regionKeys.map((k) => [
      k,
      {
        stiff_sore_worst: state.worst[k] ?? 0,
        stiff_sore_days_7: state.days[k] ?? 0,
        descriptors: state.descriptors[k] ?? [],
        trajectory: state.trajectory[k] ?? null,
        screened_out: state.screenedOut[k] === true,
      },
    ])
  );
}
